import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Send } from "lucide-react";
import { saveInteraction } from "@/data/interactionsStore";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  candidate: {
    id: string;
    firstName: string;
    lastName: string;
    partyAcronym: string;
    region: string;
    province?: string;
  };
  onSuccess: () => void;
}

export default function CandidateContactDialog({ isOpen, onClose, candidate, onSuccess }: Props) {
  const [formData, setFormData] = useState({
    senderFirstName: "",
    senderLastName: "",
    senderEmail: "",
    senderPhone: "",
    subject: "",
    message: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.senderFirstName || !formData.senderLastName || !formData.senderEmail || !formData.subject || !formData.message) {
      alert("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    saveInteraction({
      type: "candidate",
      candidateId: candidate.id,
      candidateName: `${candidate.firstName} ${candidate.lastName}`,
      partyAcronym: candidate.partyAcronym,
      senderFirstName: formData.senderFirstName,
      senderLastName: formData.senderLastName,
      senderEmail: formData.senderEmail,
      senderPhone: formData.senderPhone,
      region: candidate.region,
      province: candidate.province || "",
      subject: formData.subject,
      message: formData.message,
    });

    onSuccess();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] rounded-3xl p-0 overflow-hidden">
        <div className="h-2 w-full bg-primary" />
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-2xl font-display text-primary flex items-center gap-2">
            <Mail className="w-6 h-6" /> Contacter le candidat
          </DialogTitle>
          <DialogDescription className="font-body text-muted-foreground mt-1">
            Envoyez un message direct à <strong>{candidate.firstName} {candidate.lastName}</strong>.
            Le candidat recevra une notification par email.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col max-h-[80vh]">
          <div className="px-6 py-4 overflow-y-auto space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Prénom *</label>
                <input
                  type="text"
                  name="senderFirstName"
                  required
                  value={formData.senderFirstName}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                  placeholder="Votre prénom"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Nom *</label>
                <input
                  type="text"
                  name="senderLastName"
                  required
                  value={formData.senderLastName}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                  placeholder="Votre nom"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Email *</label>
                <input
                  type="email"
                  name="senderEmail"
                  required
                  value={formData.senderEmail}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                  placeholder="votre@email.com"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Téléphone</label>
                <input
                  type="tel"
                  name="senderPhone"
                  pattern="^[+]?[0-9]{10,}$"
                  title="num incorrect"
                  value={formData.senderPhone}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                  placeholder="Optionnel"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase">Sujet *</label>
              <input
                type="text"
                name="subject"
                required
                value={formData.subject}
                onChange={handleChange}
                className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                placeholder="Sujet de votre message"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase">Message *</label>
              <textarea
                name="message"
                required
                rows={4}
                value={formData.message}
                onChange={handleChange}
                className="w-full p-3 rounded-xl border border-input bg-background text-sm resize-none"
                placeholder="Écrivez votre message ici..."
              />
            </div>
          </div>

          <div className="p-6 border-t border-border/50 bg-muted/20 shrink-0 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="h-11 rounded-xl px-6">
              Annuler
            </Button>
            <Button type="submit" className="h-11 rounded-xl px-8 font-bold bg-primary shadow-md hover:scale-105 transition-transform gap-2">
              <Send className="w-4 h-4" /> Envoyer le message
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
